<template>
  <app-modal
    v-bind="$attrs"
    ref="modal"
    :title="isEdit ? '仮想ネットワーク変更' : '仮想ネットワーク追加'"
    class="modal-container"
    content-class="modal-medium"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- L2/L3 -->
                <v-row>
                  <v-col>
                    <template v-if="isEdit">
                      <app-label-group
                        label="L2/L3"
                        :value="virtualNetwork?.vnType"
                      />
                    </template>
                    <template v-else>
                      <app-badge
                        item="L2/L3"
                        :isRequired="false"
                        :isInfo="false"
                      />
                      <v-radio-group v-model="form.vnType" inline hide-details>
                        <v-radio label="L3" value="L3" />
                        <v-radio label="L2" value="L2" />
                      </v-radio-group>
                    </template>
                  </v-col>
                </v-row>

                <!-- 仮想ネットワーク(VN)名 -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="仮想ネットワーク(VN)名"
                      :isRequired="true"
                      :msg="
                        $filter.message('info_max_length', { maxLength: 20 })
                      "
                    />
                    <validation-provider
                      name="vnName"
                      v-model="form.vnName"
                      label="仮想ネットワーク(VN)名"
                      :rules="{ required: true, max: { length: 20 } }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- VNコード -->
                <v-row>
                  <v-col>
                    <app-label-group
                      label="VNコード"
                      :value="isEdit ? virtualNetwork?.vnCode : 'unnumbered'"
                    />
                  </v-col>
                </v-row>

                <!-- VLAN指定 / VLAN -->
                <template v-if="form.vnType === 'L2'">
                  <v-row class="mt-4">
                    <v-col>
                      <template v-if="isEdit">
                        <app-label-group
                          label="VLAN"
                          :value="
                            virtualNetwork?.vlanIdType === 'SPECIFIED'
                              ? virtualNetwork.vlanId
                              : $filter.enumTo(
                                  virtualNetwork?.vlanIdType,
                                  'vlanIdType'
                                )
                          "
                        />
                      </template>
                      <template v-else>
                        <app-badge
                          item="VLAN"
                          :isRequired="form.vlanIdType === 'SPECIFIED'"
                          :isInfo="form.vlanIdType === 'SPECIFIED'"
                          :msg="
                            form.vlanIdType === 'SPECIFIED'
                              ? $filter.message('info_half_width_num', {
                                  max: 4094,
                                })
                              : ''
                          "
                        />
                        <v-radio-group v-model="form.vlanIdType" hide-details>
                          <v-radio value="SPECIFIED">
                            <template #label>
                              <div class="d-flex align-center">
                                <v-sheet min-width="4rem">指定：</v-sheet>
                                <validation-provider
                                  v-model.number="form.vlanId"
                                  name="vlanId"
                                  label="VLAN指定"
                                  :rules="
                                    form.vlanIdType === 'SPECIFIED'
                                      ? {
                                          required: true,
                                          numeric: true,
                                          between: { min: 1, max: 4094 },
                                        }
                                      : undefined
                                  "
                                  v-slot="{
                                    componentField,
                                    errorMessage,
                                    meta,
                                  }"
                                >
                                  <app-form-input-number
                                    v-bind="componentField"
                                    class="ml-3"
                                    :disabled="form.vlanIdType !== 'SPECIFIED'"
                                    :hide-details="!errorMessage"
                                    :error-messages="errorMessage"
                                    :field-meta="meta"
                                  />
                                </validation-provider>
                              </div>
                            </template>
                          </v-radio>
                          <v-radio label="Untag" value="UNTAG" />
                          <v-radio label="Other" value="OTHER" />
                        </v-radio-group>
                      </template>
                    </v-col>
                  </v-row>
                </template>

                <!-- 利用可能VNコネクト数 -->
                <v-row>
                  <v-col>
                    <app-label-group
                      label="利用可能VNコネクト数"
                      :value="`${upperLimit}回線`"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <!-- FOOTER -->
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" variant="flat" color="primary">
                  {{ isEdit ? "変更" : "追加" }}
                </v-btn>
                <v-btn variant="outlined" color="primary" @click="cancel">
                  キャンセル
                </v-btn>
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type {
  VirtualNetworkEntity,
  VirtualNetworkPostEntity,
  VirtualNetworkUpdateEntity,
} from "@/apis/VirtualNetworkApi";
import { ApiNotFoundError } from "@/error/errors";
import type { UpperLimitEntity } from "@/apis/UpperLimitApi";
import AppLabelGroup from "@/components/AppLabelGroup.vue";

export default defineComponent({
  name: "VirtualNetworkEdit",
  components: { AppLabelGroup },
  props: {
    /** 変更対象の仮想ネットワーク情報（変更時のみ必須） */
    virtualNetwork: {
      type: Object as PropType<VirtualNetworkEntity>,
      required: false,
    },
    /** 仮想ネットワーク設定排他情報 */
    vnKeyId: {
      type: String as PropType<string>,
      required: true,
    },
  },
  data() {
    const form: VirtualNetworkUpdateEntity | VirtualNetworkPostEntity = this
      .virtualNetwork
      ? // 変更
        {
          vnType: this.virtualNetwork.vnType,
          vnName: this.virtualNetwork.vnName,
          vlanIdType:
            this.virtualNetwork.vnType === "L2"
              ? this.virtualNetwork.vlanIdType
              : null,
          vlanId: this.virtualNetwork.vlanId,
          vnKeyId: this.vnKeyId,
        }
      : // 新規
        {
          vnType: "L3",
          vnName: "",
          vlanIdType: "SPECIFIED",
          vlanId: null,
          vnKeyId: this.vnKeyId,
        };

    return {
      isLoaded: false,
      form,
      modeOptions: [
        { item: "L3", name: "L3" },
        { item: "L2", name: "L2" },
      ],
      upperLimitL2: 100,
      upperLimitL3: 500,
    };
  },
  async mounted() {
    try {
      const vnConnectUpperLimit = (await this.$api.upperLimit.getUpperLimit())
        .upperLimitList;
      // 仮想ネットワークあたりのVNコネクト数(L2)が存在しない場合は100固定
      const vnConnectLimit2 = vnConnectUpperLimit.find(
        (e: UpperLimitEntity) =>
          e.upperLimitItemName === "仮想ネットワークあたりのVNコネクト数(L2)"
      );
      this.upperLimitL2 = vnConnectLimit2 ? vnConnectLimit2.upperLimit : 100;
      // 仮想ネットワークあたりのVNコネクト数(L3)が存在しない場合は500固定
      const vnConnectLimit3 = vnConnectUpperLimit.find(
        (e: UpperLimitEntity) =>
          e.upperLimitItemName === "仮想ネットワークあたりのVNコネクト数(L3)"
      );
      this.upperLimitL3 = vnConnectLimit3 ? vnConnectLimit3.upperLimit : 500;
    } catch (e) {
      // 404が返ってきた場合はデフォルト値のままとする。
      if (e instanceof ApiNotFoundError) {
        return;
      }
      throw e;
    } finally {
      this.isLoaded = true;
    }
  },
  computed: {
    /** true: 変更, false: 新規 */
    isEdit(): boolean {
      return !!this.virtualNetwork;
    },
    /** VNコネクト数(上限数) */
    upperLimit(): number {
      if (this.virtualNetwork) {
        return this.virtualNetwork.upperLimitVnConnect;
      } else {
        const vnType = (this.form as VirtualNetworkPostEntity).vnType;
        switch (vnType) {
          case "L2":
            return this.upperLimitL2;
          case "L3":
            return this.upperLimitL3;
          default:
            throw new Error(`unknown vnType. ${vnType}`);
        }
      }
    },
  },
  methods: {
    async submit() {
      if (this.isEdit) {
        await this.$confirm(
          "仮想ネットワーク情報を変更します。よろしいですか？"
        );
        await this.$api.virtualNetwork.update(
          this.virtualNetwork!.vnCode,
          this.form as VirtualNetworkUpdateEntity
        );
      } else {
        await this.$confirm("仮想ネットワークを追加します。よろしいですか？");
        const form = this.form as VirtualNetworkPostEntity;
        await this.$api.virtualNetwork.post({
          ...form,
          vlanIdType: form.vnType === "L2" ? form.vlanIdType : null,
          vlanId:
            form.vnType === "L2" && form.vlanIdType === "SPECIFIED"
              ? form.vlanId
              : null,
        });
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>
